import { useState } from "react";
import Layout from "../components/Layout";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid, PieChart, Pie, Legend,
} from "recharts";
import {
  Buildings, CurrencyDollar, Cube, ChartPieSlice, Calendar, ListChecks, Warning, MapPin, BookOpen, ClipboardText, Flask, CheckCircle,
} from "@phosphor-icons/react";
import data from "../data/assetIntelligenceV2.json";
import { Kpi, Panel, TabBar, AutoTable, fmt$, fmtK, fmtNum, NAVY, BLUE, GROUP_COLORS } from "../components/AIV2";

const C = data.client;

const TABS = [
  { id: "summary", label: "Executive Summary", icon: ClipboardText },
  { id: "budget", label: "Budget Forecast", icon: ChartPieSlice },
  { id: "immediate", label: "Immediate Attention", icon: Warning },
  { id: "plan12", label: "12-Month Plan", icon: Calendar },
  { id: "plan24", label: "24-Month Plan", icon: Calendar },
  { id: "sites", label: "By Site", icon: MapPin },
  { id: "library", label: "Cost Library", icon: BookOpen },
  { id: "method", label: "Methodology", icon: ListChecks },
];

const CONF_COLORS = { "High / Very High": "#059669", Medium: "#D97706", Low: "#DC2626" };

export default function AssetIntelligenceV2Client() {
  const [tab, setTab] = useState("summary");
  const kpi = C.dashboard_kpis;

  const groupBudget = C.budget_by_group.filter((g) => g["Total 24 Month Budget"] > 0).map((g) => ({ name: g["Asset Group"], v12: g["0-12 Month Budget"], v24: g["12-24 Month Budget"] }));
  const siteBudget = C.budget_by_site.slice(0, 12).map((s) => ({ name: s["Site"], value: s["Total 24 Month Budget"] }));
  const forecast = C.ten_year_forecast.map((r) => ({ year: r["Year"], value: r["Forecast Renewal Budget"] }));
  const confidence = C.confidence.map((r) => ({ name: r["Confidence Band"], value: r["Asset Count"] }));

  return (
    <Layout wide>
      <div data-testid="aiv2-client-page">
        {/* Cover header */}
        <div className="mb-6 overflow-hidden rounded-sm border border-neutral-200 bg-[#0A0A0A] p-6 text-white shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0055FF]">Asset Intelligence — V2</p>
              <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-black tracking-tighter">{C.client_name}</h1>
              <p className="mt-1 text-sm text-neutral-400">Asset Intelligence &amp; Capital Planning · {C.subtitle}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#D97706]/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#F5B544]" data-testid="aiv2-sample-badge">
              <Flask size={14} weight="fill" /> Sample · Isolated
            </span>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <CoverStat icon={Cube} label="Assets Managed" value={fmtNum(C.cover.assets_managed)} />
            <CoverStat icon={CurrencyDollar} label="Replacement Value" value={fmt$(C.cover.replacement_value)} />
            <CoverStat icon={CurrencyDollar} label="0–12 Month Budget" value={fmt$(C.cover.budget_0_12)} accent="text-[#F5B544]" />
            <CoverStat icon={CurrencyDollar} label="12–24 Month Budget" value={fmt$(C.cover.budget_12_24)} accent="text-[#5B9DFF]" />
          </div>
        </div>

        {/* Renewal category cards */}
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {C.renewal_categories.map((r, i) => (
            <div key={r.category} className="rounded-sm border border-neutral-200 bg-white p-4 shadow-sm" data-testid="aiv2-renewal-card">
              <div className={`mb-1.5 h-1 w-10 rounded-full ${i === 0 ? "bg-[#DC2626]" : i === 1 ? "bg-[#D97706]" : "bg-[#059669]"}`} />
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{r.category}</p>
              <p className="mt-1 font-heading text-2xl font-black tracking-tight text-[#0A0A0A]">{fmtNum(r.assets)} <span className="text-base font-bold text-neutral-400">assets</span></p>
              <p className="text-sm font-semibold text-[#0055FF]">{fmt$(r.budget)}</p>
            </div>
          ))}
        </div>

        <TabBar tabs={TABS} active={tab} onChange={setTab} testidPrefix="aiv2-client-tab" />

        {tab === "summary" && (
          <div className="space-y-4">
            <Panel title="Executive Summary" testid="aiv2-exec-summary">
              <p className="text-sm leading-relaxed text-neutral-700">{C.executive_summary}</p>
            </Panel>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Panel title="Recommended Budget Scenarios" testid="aiv2-scenarios">
                <div className="space-y-3">
                  {C.scenarios.map((s) => (
                    <div key={s["Scenario"]} className="rounded-sm border border-neutral-200 p-3.5">
                      <div className="flex items-center justify-between">
                        <p className="font-heading font-black tracking-tight text-[#0A0A0A]">{s["Scenario"]}</p>
                        <span className="font-semibold text-[#0055FF]">{fmt$(s["Budget"])}</span>
                      </div>
                      <p className="mt-1 text-xs text-neutral-500">{fmtNum(s["Included Assets"])} assets · {s["Purpose"]} · <span className="italic">{s["Presentation Use"]}</span></p>
                    </div>
                  ))}
                </div>
              </Panel>
              <Panel title="Recommended Next Steps" testid="aiv2-next-steps">
                <ul className="space-y-2.5">
                  {C.next_steps.map((step) => (
                    <li key={step} className="flex items-start gap-2 text-sm text-neutral-700">
                      <CheckCircle size={17} className="mt-0.5 shrink-0 text-[#059669]" weight="fill" /> {step}
                    </li>
                  ))}
                </ul>
              </Panel>
            </div>
            <Panel title="Board Summary" subtitle="Capital renewal planning baseline" testid="aiv2-board">
              <p className="text-sm leading-relaxed text-neutral-700">{C.board_report.commentary}</p>
            </Panel>
          </div>
        )}

        {tab === "budget" && (
          <div className="space-y-4">
            <Panel title="10-Year Renewal Budget Forecast" testid="aiv2-forecast-chart">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={forecast}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmt$(v)} />
                  <Bar dataKey="value" fill={BLUE} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Panel title="Budget by Asset Group" subtitle="0–12 vs 12–24 month" testid="aiv2-group-budget-chart">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={groupBudget} layout="vertical" margin={{ left: 30 }}>
                    <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmt$(v)} />
                    <Legend />
                    <Bar dataKey="v12" name="0–12 mo" stackId="a" fill={BLUE} />
                    <Bar dataKey="v24" name="12–24 mo" stackId="a" fill={NAVY} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="Data Confidence" subtitle="Model completeness by asset" testid="aiv2-confidence-chart">
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie data={confidence} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={(e) => `${e.name}: ${e.value}`}>
                      {confidence.map((e, i) => <Cell key={i} fill={CONF_COLORS[e.name] || GROUP_COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Panel>
            </div>
            <Panel title="Top Sites by 24-Month Budget" testid="aiv2-site-chart">
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={siteBudget} layout="vertical" margin={{ left: 40 }}>
                  <XAxis type="number" tickFormatter={fmtK} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => fmt$(v)} />
                  <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                    {siteBudget.map((e, i) => <Cell key={i} fill={GROUP_COLORS[i % GROUP_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Budget Summary by Asset Group" testid="aiv2-group-budget-table"><AutoTable rows={C.budget_by_group} search={false} maxHeight="26rem" /></Panel>
          </div>
        )}

        {tab === "immediate" && <Panel title="Immediate Attention" subtitle="Highest-priority assets for site validation (0–6 months)" testid="aiv2-immediate-table"><AutoTable rows={C.immediate_attention} maxHeight="38rem" /></Panel>}
        {tab === "plan12" && <Panel title="12-Month Plan" subtitle={`${C.plan_12.length} assets for the next annual capital planning cycle`} testid="aiv2-plan12-table"><AutoTable rows={C.plan_12} maxHeight="38rem" /></Panel>}
        {tab === "plan24" && <Panel title="24-Month Plan" subtitle={`${C.plan_24.length} assets in the forward renewal pipeline`} testid="aiv2-plan24-table"><AutoTable rows={C.plan_24} maxHeight="38rem" /></Panel>}
        {tab === "sites" && <Panel title="Budget Summary by Site" testid="aiv2-sites-table"><AutoTable rows={C.budget_by_site} maxHeight="38rem" /></Panel>}
        {tab === "library" && <Panel title="GBE Asset Cost Library" subtitle="Central replacement cost, material/labour split & expected life by asset type" testid="aiv2-library-table"><AutoTable rows={C.cost_library} maxHeight="38rem" /></Panel>}
        {tab === "method" && (
          <div className="space-y-4">
            <Panel title="Methodology" testid="aiv2-method-table"><AutoTable rows={C.methodology} search={false} maxHeight="auto" /></Panel>
            <Panel title="Assumptions &amp; Disclaimer" testid="aiv2-assumptions-table"><AutoTable rows={C.assumptions} search={false} maxHeight="auto" /></Panel>
          </div>
        )}

        <div className="mt-6 rounded-sm border border-[#D97706]/30 bg-[#D97706]/5 p-4" data-testid="aiv2-notice">
          <p className="text-xs leading-relaxed text-neutral-600"><span className="font-bold text-[#D97706]">Important Notice — </span>{C.cover.notice} <span className="font-semibold">This is sample data, isolated from live AroFlo / site data.</span></p>
        </div>
      </div>
    </Layout>
  );
}

function CoverStat({ icon: Icon, label, value, accent = "text-white" }) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/5 p-3" data-testid="aiv2-cover-stat">
      <div className="mb-1 flex items-center gap-1.5 text-neutral-400"><Icon size={14} /><span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span></div>
      <p className={`font-heading text-xl font-black tracking-tight ${accent}`}>{value}</p>
    </div>
  );
}
